import React, { useState, useEffect, useCallback } from 'react';
import mediaService from '../../../services/mediaService';

interface Media {
  id: string;
  url: string;
  description: string;
  category: string;
  created_at: string;
  resource_type?: string; // 'image' ou 'video'
}

interface PhotoManagerProps {
  category: string;
}

const PhotoManager: React.FC<PhotoManagerProps> = ({ category }) => {
  const [medias, setMedias] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Déplacer fetchMedias en dehors du useEffect pour le rendre accessible partout dans le composant
  const fetchMedias = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const photos = await mediaService.getByCategory(category);
      const formatted: Media[] = photos.map((photo) => ({
        id: photo._id.toString(),
        url: photo.url,
        description: photo.description || '',
        category: typeof photo.category === 'string' ? photo.category : category,
        created_at: typeof photo.created_at === 'string' ? photo.created_at : photo.created_at.toISOString(),
        resource_type: photo.resource_type || 'image',
      }));

      if (formatted.length > 0) {
        setMedias(formatted);
      } else {
        setMedias([]);
        setError('Aucun média trouvé pour cette catégorie');
      }
    } catch (error: any) {
      setError(error.message || 'Erreur lors du chargement des photos');
      console.error('Erreur de chargement des photos:', error);
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Charger les médias de la catégorie sélectionnée
  useEffect(() => {
    fetchMedias();
  }, [fetchMedias]);

  // Gérer la sélection de fichiers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(e.target.files);
      setUploadError(null); // Réinitialiser les erreurs précédentes
    }
  };

  // Gérer le glisser-déposer
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Vérifier les types de fichiers
      const isValid = Array.from(e.dataTransfer.files).every(file => {
        if (category.toLowerCase() === 'tournages') {
          return file.type.startsWith('video/') || file.type.startsWith('image/');
        } else {
          return file.type.startsWith('image/');
        }
      });

      if (!isValid) {
        setUploadError(category.toLowerCase() === 'tournages' 
          ? 'Seules les vidéos et les images sont acceptées pour les tournages.' 
          : 'Seules les images sont acceptées pour cette catégorie.');
        return;
      }

      setSelectedFiles(e.dataTransfer.files);
      setUploadError(null);
    }
  }, [category]);

  // Télécharger les médias (photos ou vidéos) vers Supabase Storage + table media
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFiles || selectedFiles.length === 0) {
      setUploadError(category.toLowerCase() === 'tournages' ? 'Veuillez sélectionner au moins une vidéo' : 'Veuillez sélectionner au moins une photo');
      return;
    }

    // Vérifier la taille des fichiers
    const MAX_FILE_SIZE_MB = 300; // 300 MB maximum pour les vidéos
    const MAX_IMAGE_SIZE_MB = 10; // 10 MB maximum pour les images

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileSizeMB = file.size / (1024 * 1024);
      const isVideo = file.type.startsWith('video/');

      if ((isVideo && fileSizeMB > MAX_FILE_SIZE_MB) ||
          (!isVideo && fileSizeMB > MAX_IMAGE_SIZE_MB)) {
        setUploadError(`Le fichier "${file.name}" est trop volumineux (${fileSizeMB.toFixed(2)} MB). 
          Taille maximale: ${isVideo ? MAX_FILE_SIZE_MB : MAX_IMAGE_SIZE_MB} MB.`);
        return;
      }
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);

    try {
      // Utiliser la bonne catégorie selon le mapping
      let categoryToSend = category;
      if (category.toLowerCase() === 'voitures') {
        categoryToSend = 'Voitures';
      }

      await mediaService.upload(selectedFiles, categoryToSend);

      setUploadProgress(100);
      setUploadSuccess(true);

      // Réinitialiser le formulaire
      setSelectedFiles(null);
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Rafraîchir la liste des médias
      await fetchMedias();
    } catch (error: any) {
      console.error('Erreur détaillée:', error);
      setUploadError(error.message || 'Erreur lors du téléchargement');
    } finally {
      setUploadLoading(false);
    }
  };

  // Supprimer un média (photo ou vidéo)
  const handleDelete = async (id: string, isVideo: boolean = false) => {
    if (!id) {
      console.error('Erreur: ID du média non défini');
      alert('Impossible de supprimer ce média: ID manquant');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${isVideo ? 'cette vidéo' : 'cette photo'} ?`)) {
      return;
    }

    try {
      await mediaService.remove(id);
      // Mettre à jour la liste des médias
      setMedias(prevMedias => prevMedias.filter(media => media.id !== id));
    } catch (error: any) {
      console.error('Erreur de suppression:', error);
      alert(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-white font-serif">
          {category === 'portrait' && 'Portraits'}
          {category === 'paysages' && 'Paysages'}
          {category === 'Voitures' && 'Voitures'}
          {category === 'tournages' && 'Tournages'}
        </h2>
        
        {/* Message d'information pour les vidéos volumineuses */}
        {category.toLowerCase() === 'tournages' && (
          <div className="mb-4 p-3 bg-blue-900/30 text-blue-200 rounded-md border border-blue-800">
            <h3 className="font-medium mb-1">Téléchargement de vidéos</h3>
            <ul className="list-disc pl-5 text-sm">
              <li>Taille maximale: 300 MB</li>
              <li>Le téléchargement peut prendre plusieurs minutes</li>
              <li>Ne fermez pas cette fenêtre pendant le téléchargement</li>
              <li>Pour les vidéos très volumineuses, il est recommandé de les compresser avant de les télécharger</li>
            </ul>
          </div>
        )}
        
        {/* Formulaire de téléchargement */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-md mb-6 border border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-white">{category.toLowerCase() === 'tournages' ? 'Ajouter de nouvelles vidéos' : 'Ajouter de nouvelles photos'}</h3>
          
          <form onSubmit={handleUpload} className="mb-6">
            <div 
              className={`mb-4 border-2 border-dashed p-8 rounded-lg text-center transition-colors ${
                isDragging ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <label className="block text-gray-300 mb-4">
                {category.toLowerCase() === 'tournages' 
                  ? 'Glissez-déposez vos vidéos ici ou cliquez pour sélectionner' 
                  : 'Glissez-déposez vos photos ici ou cliquez pour sélectionner'}
              </label>
              <input
                id="file-upload"
                type="file"
                accept={category.toLowerCase() === 'tournages' ? 'video/*,image/*' : 'image/*'}
                multiple
                onChange={handleFileChange}
                className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-white"
              />
              {selectedFiles && selectedFiles.length > 0 && (
                <div className="mt-4 text-left">
                  <p className="text-gray-300 font-semibold">{selectedFiles.length} fichier(s) sélectionné(s):</p>
                  <ul className="list-disc pl-5 text-gray-400 text-sm max-h-32 overflow-y-auto">
                    {Array.from(selectedFiles).map((file, index) => (
                      <li key={`file-${file.name}-${index}`}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {uploadError && (
              <div className="mb-4 p-3 bg-red-900/30 text-red-200 rounded-md border border-red-800">
                {uploadError}
              </div>
            )}
            
            {uploadSuccess && (
              <div className="mb-4 p-3 bg-green-900/30 text-green-200 rounded-md border border-green-800">
                {category.toLowerCase() === 'tournages' ? 'Vidéos téléchargées avec succès !' : 'Photos téléchargées avec succès !'}
              </div>
            )}
            
            {uploadLoading && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-200">Téléchargement en cours</span>
                  <span className="text-sm font-medium text-blue-200">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {uploadProgress < 100 
                    ? "Veuillez patienter pendant le téléchargement..." 
                    : "Traitement en cours..."}
                </p>
              </div>
            )}
            
            <button
              type="submit"
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 transition-colors"
              disabled={uploadLoading}
            >
              {uploadLoading ? 'Téléchargement en cours...' : 'Télécharger'}
            </button>
            <p className="mt-1 text-sm text-gray-400">
              Vous pouvez sélectionner plusieurs fichiers à la fois
            </p>
          </form>
        </div>
        
        {/* Liste des photos */}
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-2 text-gray-400">{category.toLowerCase() === 'tournages' ? 'Chargement des vidéos...' : 'Chargement des photos...'}</p>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-900/30 text-red-200 rounded-md border border-red-800">
            {error}
          </div>
        ) : medias.length === 0 ? (
          <div className="p-4 bg-gray-800 text-gray-300 rounded-md border border-gray-700">
            {category.toLowerCase() === 'tournages' ? 'Aucune vidéo trouvée dans cette catégorie.' : 'Aucune photo trouvée dans cette catégorie.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {medias.map((media, index) => (
              <div key={media.id} className="bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-700">
                <div className="relative">
                  {media.url.includes('.mp4') || media.url.includes('.webm') || media.url.includes('.mov') || media.resource_type === 'video' ? (
                    <video
                      src={media.url}
                      controls
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <img
                      src={media.url}
                      alt={media.description || 'Media'}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2 flex space-x-2">
                    <button
                      onClick={() => handleDelete(media.id, media.url.includes('.mp4') || media.url.includes('.webm') || media.url.includes('.mov') || media.resource_type === 'video')}
                      className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm text-gray-300">
                    {new Date(media.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoManager;
